<script setup lang="ts">
import {
  buildMyPageLink,
  CheckboxComponent,
  copyTextToClipboard,
  type Creator,
  CreatorStatusEnum,
  formatDateOnly,
  formatDateTimeLong,
  formatDateTimeRelative,
  ProfileImageComponent,
  SocialMediaAccounts,
} from '@metapic-apps/shared'
import { computed } from 'vue'

import CopyIcon from '@/assets/icons/copy.svg?component'
import EmailIcon from '@/assets/icons/mail.svg?component'
import { config, getRecruitmentUtmLabel } from '@/lib/helpers'
import {
  BoxComponent,
  FlexComponent,
  TagComponent,
  TagsCollapsibleListComponent,
  TooltipComponent,
  TypographyComponent,
} from '@/ui-library'

const props = withDefaults(
  defineProps<{
    creator: Creator
    isSelected?: boolean
  }>(),
  {
    isSelected: false,
  },
)
const emits = defineEmits<{
  toggleSelect: [creator: Creator]
}>()

const creatorLink = computed(() => `/creators/${props.creator.id}/info`)

const myPage = computed(() => buildMyPageLink(config.myPagePublicUrl, props.creator.myPageSlug))

const recruitmentUtmLabel = computed(() => getRecruitmentUtmLabel(props.creator))

const showRecruitmentUtm = computed(
  () => props.creator.tags.length === 0 && props.creator.status === CreatorStatusEnum.NotApproved && recruitmentUtmLabel.value !== null,
)

const RECRUITMENT_UTM_TRUNCATION_THRESHOLD = 40

const campaignStatusMap: Partial<Record<CreatorStatusEnum, string>> = {
  [CreatorStatusEnum.NotApproved]: 'Undecided',
  [CreatorStatusEnum.Approved]: 'Accepted',
  [CreatorStatusEnum.Banned]: 'Suspended',
}
const tagsList: { content: string; id: string | number }[] = props.creator.tags.map((tag) => ({
  content: tag.name,
  id: tag.id,
}))

const toggleSelect = () => {
  emits('toggleSelect', props.creator)
}

const handleCardClick = (event: MouseEvent) => {
  if (props.isSelected) {
    event.preventDefault()
    toggleSelect()
  }
}
</script>
<template>
  <router-link :to="creatorLink" @click="handleCardClick">
    <BoxComponent :p="24" class="list-item">
      <FlexComponent justify="space-between" align="center" :gap="16">
        <FlexComponent is-child flex="0">
          <div class="creator-list-item__avatar-wrapper" @click.stop.prevent="toggleSelect">
            <div class="creator-list-item__avatar-clip">
              <ProfileImageComponent :image="creator.avatar" :name="creator.username" :alt="creator.username" :size="64" />
            </div>
            <div class="creator-list-item__checkbox-overlay" :class="{ 'creator-list-item__checkbox-overlay--visible': isSelected }">
              <CheckboxComponent :model-value="isSelected" />
            </div>
          </div>
        </FlexComponent>
        <BoxComponent w="30%">
          <FlexComponent align="center" :gap="8">
            <TypographyComponent variant="title-large">
              {{ creator.firstName || creator.username }}
            </TypographyComponent>
            <TooltipComponent>
              <template #default>
                <FlexComponent align="center">
                  <el-icon :size="18" color="var(--primary)">
                    <EmailIcon />
                  </el-icon>
                </FlexComponent>
              </template>
              <template #content>
                <TypographyComponent variant="title-medium">Email</TypographyComponent>
                <FlexComponent :gap="4" align="center">
                  <TypographyComponent variant="body-medium" color="var(--secondary-48)">
                    {{ creator.email }}
                  </TypographyComponent>
                  <TooltipComponent trigger="click" placement="top" :auto-close="600">
                    <template #default>
                      <el-icon
                        :size="18"
                        color="var(--primary)"
                        class="creator-list-item__copy-icon"
                        @click.stop.prevent="copyTextToClipboard(creator.email)"
                      >
                        <CopyIcon />
                      </el-icon>
                    </template>
                    <template #content>
                      <TypographyComponent variant="label-large">Copied!</TypographyComponent>
                    </template>
                  </TooltipComponent>
                </FlexComponent>
              </template>
            </TooltipComponent>
          </FlexComponent>

          <BoxComponent :mt="6" :mb="8" :max-w="280">
            <TagsCollapsibleListComponent v-if="creator.tags.length > 0" :tags="tagsList" />
            <TooltipComponent
              v-else-if="showRecruitmentUtm"
              placement="bottom"
              :disabled="(recruitmentUtmLabel?.length ?? 0) <= RECRUITMENT_UTM_TRUNCATION_THRESHOLD"
            >
              <template #default>
                <TypographyComponent variant="body-small" class="creator-list-item__utm-label" text-truncate>
                  {{ recruitmentUtmLabel }}
                </TypographyComponent>
              </template>
              <template #content>
                <BoxComponent :mb="4">
                  <TypographyComponent variant="title-medium" :font-weight="600">Recruitment</TypographyComponent>
                </BoxComponent>
                <TypographyComponent variant="body-medium">{{ recruitmentUtmLabel }}</TypographyComponent>
              </template>
            </TooltipComponent>
          </BoxComponent>
          <FlexComponent align="center">
            <TypographyComponent variant="body-small" color="var(--secondary-48)" class="creator-list-item__footer-item">
              ID: {{ creator.id }}
            </TypographyComponent>
            <TooltipComponent v-if="creator.createdAt">
              <template #default>
                <TypographyComponent variant="body-small" color="var(--secondary-48)" class="creator-list-item__footer-item">
                  Registered {{ formatDateTimeRelative(creator.createdAt) }}
                </TypographyComponent>
              </template>
              <template #content>
                <TypographyComponent variant="body-small">{{ formatDateTimeLong(creator.createdAt) }}</TypographyComponent>
              </template>
            </TooltipComponent>
          </FlexComponent>
        </BoxComponent>
        <BoxComponent w="21%">
          <TypographyComponent variant="label-medium" text-align="center">Socials</TypographyComponent>
          <BoxComponent :mt="3" :min-h="24">
            <SocialMediaAccounts :social-media="creator.socials" :my-page="myPage" show-all-types show-extended-data :icon-size="18" />
          </BoxComponent>
        </BoxComponent>
        <BoxComponent w="13%">
          <TypographyComponent variant="label-medium" text-align="center">Client</TypographyComponent>
          <TypographyComponent variant="title-large" text-align="center">{{ creator.client?.name }}</TypographyComponent>
        </BoxComponent>
        <BoxComponent w="13%">
          <TypographyComponent variant="label-medium" text-align="center">Revenue tier</TypographyComponent>
          <TypographyComponent variant="title-large" text-align="center">{{ creator.revenueTier?.name }}</TypographyComponent>
        </BoxComponent>
        <BoxComponent w="13%">
          <TypographyComponent variant="label-medium" text-align="center">Latest payment</TypographyComponent>
          <TypographyComponent variant="title-large" text-align="center">
            {{ creator.latestPaymentDate ? formatDateOnly(creator.latestPaymentDate) : '-' }}
          </TypographyComponent>
        </BoxComponent>
        <BoxComponent w="10%">
          <FlexComponent justify="end">
            <TagComponent
              :extra-class="`creator-list-item__tag creator-list-item__tag-${props.creator.status.replace(' ', '-').toLowerCase()}`"
            >
              {{ campaignStatusMap[creator.status] }}
            </TagComponent>
          </FlexComponent>
        </BoxComponent>
      </FlexComponent>
    </BoxComponent>
  </router-link>
</template>
<style lang="scss" scoped>
.list-item {
  position: relative;

  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);

  cursor: pointer;
  transition: background-color var(--transition) ease;

  &:hover {
    background-color: var(--secondary-2);

    .creator-list-item__checkbox-overlay {
      opacity: 1;
    }
  }
}

.creator-list-item__footer-item {
  &:not(:last-child):after {
    content: '|';
    margin: 0 8px;
  }
}

.creator-list-item__avatar-wrapper {
  position: relative;
  width: 64px;
  height: 64px;
  cursor: pointer;
}

.creator-list-item__avatar-clip {
  width: 64px;
  height: 64px;
  border-radius: 100%;
  overflow: hidden;
}

.creator-list-item__checkbox-overlay {
  position: absolute;
  inset: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--inverted);
  border-radius: 100%;
  border: 1px solid var(--button-hover);
  opacity: 0;
  transition: opacity var(--transition) ease;

  &--visible {
    opacity: 1;
  }
}
.creator-list-item__copy-tooltip {
  height: 20px;
}
.creator-list-item__social-icon {
  height: 18px;
}
.creator-list-item__copy-icon {
  cursor: pointer;
}

.creator-list-item__utm-label {
  display: inline-block;
  max-width: 100%;
  padding: 2px 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;

  border-radius: 4px;
  border: 1px solid rgba(34, 34, 34, 0.08);
}

:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}
:deep(.el-checkbox__label) {
  display: none;
}

:deep(.creator-list-item__tag) {
  height: 32px;
  color: var(--inverted);

  &.creator-list-item__tag-not-approved {
    background: var(--secondary-48);
  }
  &.creator-list-item__tag-approved,
  &.creator-list-item__tag-validated {
    background: var(--success);
  }
  &.creator-list-item__tag-not-validated {
    background: var(--warning);
  }
  &.creator-list-item__tag-banned {
    background: var(--alert);
  }
}
</style>
